import { FC, useState } from "react";
import { Box, CircularProgress } from "@material-ui/core";
import { Snackbar } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Redirect, useHistory, useParams } from "react-router-dom";

import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import StepContainer from "containers/AddRoyaltyAccount/components/StepContainer";
import * as addRoyaltyAccountRoutes from "containers/AddRoyaltyAccount/routes";
import { removeMoneyMask } from "lib/parse";
import {
  updateCredentialSubmission,
  useAssetKYWCredentialSubmission,
} from "services/assets";
import { getCredentialSubmissionErrorMessage } from "services/credentialSubmission";
import { CredentialSubmissionFormValues } from "services/credentialSubmission/types";

import TimeForEstimation from "../TimeForEstimation";
import CredentialSubmissionDetailsForm from "./CredentialSubmissionDetailsForm";

type FormValues = Pick<
  CredentialSubmissionFormValues,
  | "attestedLtm"
  | "attestedAge"
  | "biggestSongName"
  | "biggestSongArtistName"
  | "password"
>;

interface FormValidation {
  open: boolean;
  message?: string;
}

const CredentialSubmissionDetails: FC = () => {
  const history = useHistory();
  const { royaltyPayorName, assetId } = useParams<{
    royaltyPayorName: string;
    assetId: string;
  }>();
  const assetKYWCredentialSubmissionQueries = useAssetKYWCredentialSubmission(
    assetId,
  );
  const [snackbarConfig, setSnackbarConfig] = useState<FormValidation>({
    open: false,
  });

  const asset = assetKYWCredentialSubmissionQueries.data;
  const isUpdatingCredentialSubmission =
    asset?.state === "failed_need_credentials_update";
  const royaltyPayor = asset?.credential_submission.royalty_payor;
  const royaltyPayorDisplayName =
    royaltyPayor?.display_name ?? EMPTY_VALUE_PLACEHOLDER;

  const credentialSubmission = asset?.credential_submission;
  const initialValues = {
    password: "",
    attestedLtm: credentialSubmission?.attested_ltm ?? "",
    attestedAge: credentialSubmission?.attested_age ?? "",
    biggestSongName: credentialSubmission?.biggest_song_name ?? "",
    biggestSongArtistName: credentialSubmission?.biggest_song_artist_name ?? "",
  };

  const handleCompleteCredentialSubmission = async (formValues: FormValues) => {
    try {
      const payload: CredentialSubmissionFormValues = {
        ...formValues,
        attestedLtm: removeMoneyMask(formValues.attestedLtm),
      };
      await updateCredentialSubmission(asset.id, payload);
      history.push(
        addRoyaltyAccountRoutes.connectGetCredentialsRoyaltyPayorAccountSuccess(
          royaltyPayorName,
        ),
      );
    } catch (e) {
      const credentialSubmissionErrorMessage = getCredentialSubmissionErrorMessage(
        e,
      );
      {
        setSnackbarConfig({
          open: true,
          message: credentialSubmissionErrorMessage,
        });
      }
    }
  };

  return assetKYWCredentialSubmissionQueries.isLoading ? (
    <CircularProgress />
  ) : !!asset?.credential_submission.attested_ltm &&
    !isUpdatingCredentialSubmission ? (
    <Redirect
      to={addRoyaltyAccountRoutes.connectGetCredentialsRoyaltyPayorAccountSuccess(
        royaltyPayorName,
      )}
    />
  ) : (
    <StepContainer>
      <TimeForEstimation
        royaltyPayorName={royaltyPayorDisplayName}
        hasGetCredentialPermission
      />
      <Box mt={4}>
        <CredentialSubmissionDetailsForm
          initialValues={initialValues}
          royaltyPayorDisplayName={royaltyPayorDisplayName}
          royaltyPayorUsername={royaltyPayor?.username_or_email ?? ""}
          username={asset?.credential_submission.username ?? ""}
          isUpdatingCredentialSubmission={isUpdatingCredentialSubmission}
          onSave={handleCompleteCredentialSubmission}
        />
      </Box>
      <Snackbar
        open={snackbarConfig.open}
        autoHideDuration={3000}
        onClose={() => setSnackbarConfig({ open: false })}
      >
        <Alert
          onClose={() => setSnackbarConfig({ open: false })}
          severity="error"
        >
          {snackbarConfig.message}
        </Alert>
      </Snackbar>
    </StepContainer>
  );
};

export default CredentialSubmissionDetails;
